import { useAuth } from "@/hooks/useAuth";
import { useDealsList } from "@/hooks/deals/useDeals";
import { FocusedKanban } from "@/components/deals/KanbanBoard";
import { STAGE_LABEL, SUB_STATE_LABEL, subStateLabelFor } from "@/types/deals";
import type { DealListItemDto, DealListQuery } from "@/types/deals";
import { ageSeverity, dealLabel, daysSince, formatCurrency } from "@/components/deals/dealFormat";
import { WidgetFrame } from "../../WidgetFrame";
import type { DashboardWidgetProps } from "../../types";
import { PanelRows, type PanelRowItem } from "./shared";

/**
 * Deal-pipeline panels absorbed from the retired LiaisonDealDashboard /
 * LoanAdminDashboard pages: each former dashboard section is now one
 * independently-placeable widget over the same /api/deals queries.
 */

// ---- Kanban panels ---------------------------------------------------------

export function IntakeKanbanWidget({ widget }: DashboardWidgetProps) {
  const { user } = useAuth();
  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/pipeline"
      toLabel="Full pipeline"
    >
      <FocusedKanban
        stage="Intake"
        query={{ assignedC1Id: user?.id, pageSize: 200 }}
        emptyMessage="No deals here yet."
      />
    </WidgetFrame>
  );
}

export function PreApprovalKanbanWidget({ widget }: DashboardWidgetProps) {
  const { user } = useAuth();
  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/pipeline"
      toLabel="Full pipeline"
    >
      <FocusedKanban
        stage="PreApproval"
        query={{ assignedB1Id: user?.id, pageSize: 200 }}
        emptyMessage="No deals here yet."
      />
    </WidgetFrame>
  );
}

// ---- B1 alert panels -------------------------------------------------------

export function NewlyAssignedDealsWidget({ widget }: DashboardWidgetProps) {
  const { user } = useAuth();
  const dealsQuery = useDealsList(
    { assignedB1Id: user?.id, subState: ["Applications"], pageSize: 50 },
    !!user?.id,
  );
  const rows = dealsQuery.data?.data ?? [];

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/pipeline"
      isLoading={dealsQuery.isLoading}
      isError={dealsQuery.isError}
      isEmpty={rows.length === 0}
      emptyText="Nothing waiting on you — when a C1 hands a deal to you, it lands here first."
    >
      <PanelRows
        items={rows.map((d) => ({
          id: d.id,
          primary: dealLabel(d),
          secondary: `${d.dealNumber} · ${formatCurrency(d.purchasePrice)}`,
          badge: `${daysSince(d.stageEnteredDate)}d new`,
          badgeTone: "info",
          href: `/pipeline/${d.id}`,
        }))}
      />
    </WidgetFrame>
  );
}

export function ReadyToSubmitWidget({ widget }: DashboardWidgetProps) {
  const { user } = useAuth();
  const dealsQuery = useDealsList(
    { assignedB1Id: user?.id, subState: ["ReadyToSubmit"], pageSize: 50 },
    !!user?.id,
  );
  const rows = dealsQuery.data?.data ?? [];

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/pipeline"
      isLoading={dealsQuery.isLoading}
      isError={dealsQuery.isError}
      isEmpty={rows.length === 0}
      emptyText="Nothing ready to submit — deals appear here once applications, letters, and documents are all cleared."
    >
      <PanelRows
        items={rows.map((d) => ({
          id: d.id,
          primary: dealLabel(d),
          secondary: `${SUB_STATE_LABEL[d.subState]} · ${daysSince(d.stageEnteredDate)}d`,
          href: `/pipeline/${d.id}`,
        }))}
      />
    </WidgetFrame>
  );
}

// ---- Aging panels ----------------------------------------------------------

function agingItems(rows: DealListItemDto[], limit = 8): PanelRowItem[] {
  return rows
    .map((deal) => ({ deal, severity: ageSeverity(deal) }))
    .filter((x) => x.severity !== "ok")
    .sort((a, b) => daysSince(b.deal.stageEnteredDate) - daysSince(a.deal.stageEnteredDate))
    .slice(0, limit)
    .map(({ deal, severity }) => ({
      id: deal.id,
      primary: dealLabel(deal),
      secondary: subStateLabelFor(deal.stage, deal.subState) ?? STAGE_LABEL[deal.stage],
      badge: `${daysSince(deal.stageEnteredDate)}d`,
      badgeTone: severity === "breach" ? "danger" : "warn",
      href: `/pipeline/${deal.id}`,
    }));
}

/** Which assignee filter "my deals" means depends on the hat being worn. */
function myDealsFilter(activeRole: string, userId: string | undefined): DealListQuery {
  if (!userId) return {};
  if (activeRole === "C1") return { assignedC1Id: userId };
  if (activeRole === "A1") return { assignedA1Id: userId };
  return { assignedB1Id: userId };
}

export function DealAgingMineWidget({ widget, activeRole }: DashboardWidgetProps) {
  const { user } = useAuth();
  const dealsQuery = useDealsList(
    { ...myDealsFilter(activeRole, user?.id), pageSize: 200 },
    !!user?.id,
  );
  const items = agingItems(dealsQuery.data?.data ?? []);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/pipeline"
      isLoading={dealsQuery.isLoading}
      isError={dealsQuery.isError}
      isEmpty={items.length === 0}
      emptyText="No aging deals — every active deal is within its SLA window."
    >
      <PanelRows items={items} />
    </WidgetFrame>
  );
}

export function DealAgingTeamWidget({ widget }: DashboardWidgetProps) {
  const dealsQuery = useDealsList({ pageSize: 200 });
  const items = agingItems(dealsQuery.data?.data ?? []);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/pipeline"
      isLoading={dealsQuery.isLoading}
      isError={dealsQuery.isError}
      isEmpty={items.length === 0}
      emptyText="No aging deals — every active deal is within its SLA window."
    >
      <PanelRows items={items} />
    </WidgetFrame>
  );
}

// ---- Recently created ------------------------------------------------------

export function RecentlyCreatedDealsWidget({ widget }: DashboardWidgetProps) {
  const { user } = useAuth();
  const dealsQuery = useDealsList({ assignedC1Id: user?.id, pageSize: 50 }, !!user?.id);
  const rows = (dealsQuery.data?.data ?? [])
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 8);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/pipeline"
      isLoading={dealsQuery.isLoading}
      isError={dealsQuery.isError}
      isEmpty={rows.length === 0}
      emptyText="You haven't created any deals yet."
    >
      <PanelRows
        items={rows.map((d) => ({
          id: d.id,
          primary: dealLabel(d),
          meta: `${formatCurrency(d.purchasePrice)} · ${daysSince(d.createdAt)}d ago`,
          href: `/pipeline/${d.id}`,
        }))}
      />
    </WidgetFrame>
  );
}

// ---- Underwriting queue ----------------------------------------------------

export function MyUnderwritingQueueWidget({ widget, activeRole }: DashboardWidgetProps) {
  const { user } = useAuth();
  // A1 deals carry an assignedA1Id; the A2/A3/A4 lanes work every
  // Underwriting-stage case, so they see the full stage queue.
  const query: DealListQuery =
    activeRole === "A1" && user?.id
      ? { stage: ["Underwriting"], assignedA1Id: user.id, pageSize: 100 }
      : { stage: ["Underwriting"], pageSize: 100 };
  const dealsQuery = useDealsList(query);
  const rows = dealsQuery.data?.data ?? [];

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/pipeline"
      isLoading={dealsQuery.isLoading}
      isError={dealsQuery.isError}
      isEmpty={rows.length === 0}
      emptyText="No deals in underwriting right now."
    >
      <PanelRows
        items={rows.map((d) => ({
          id: d.id,
          primary: dealLabel(d),
          secondary: `${d.dealNumber} · ${SUB_STATE_LABEL[d.subState]}`,
          badge: `${daysSince(d.stageEnteredDate)}d`,
          badgeTone: ageSeverity(d) === "breach" ? "danger" : "neutral",
          href: `/deal-flow/deals/${d.id}`,
        }))}
      />
    </WidgetFrame>
  );
}
